import { useTranslation } from 'react-i18next';

import { ChartKeySwatch } from '@/components/charts/legend';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import { formatFractionAsPercentageByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './BioscopeSceneLabelsLegend.module.scss';

interface LegendRow {
    color: string;
    label: string;
    /** The label's share of the classified images, as a fraction. */
    share: number;
}

function LegendSwatch(props: { color: string }) {
    const { color } = props;
    return <ChartKeySwatch shape="dot" color={color} className={styles.legendColorIndicator} />;
}

function LegendShare(props: { share: number }) {
    const { share } = props;
    const { i18n } = useTranslation();
    return <>{formatFractionAsPercentageByLocale(share, parseLocale(i18n.language))}</>;
}

const COLUMNS: DataTableColumnDef<LegendRow>[] = [
    {
        id: 'swatch',
        cell: ({ row }) => <LegendSwatch color={row.original.color} />,
        meta: { cellClassName: styles.swatchCell },
    },
    {
        id: 'label',
        cell: ({ row }) => row.original.label,
        meta: { cellClassName: styles.labelCell },
    },
    {
        id: 'count',
        cell: ({ row }) => <LegendShare share={row.original.share} />,
        meta: { cellClassName: styles.countCell },
    },
];

interface BioscopeSceneLabelsLegendProps {
    /** Each label's share as a fraction (0.25 for 25 %). */
    relativeCounts: number[];
    colors: string[];
    labels: string[];
}

export default function BioscopeSceneLabelsLegend(props: BioscopeSceneLabelsLegendProps) {
    const { relativeCounts = [], colors = [], labels = [] } = props;
    const rows: LegendRow[] = relativeCounts.flatMap((share, index) =>
        Number.isFinite(share) ? [{ color: colors[index], label: labels[index], share }] : []
    );

    return (
        <div className={styles.legendContainer}>
            <DataTable
                data={rows}
                columns={COLUMNS}
                getRowId={(row) => `${row.label}-${row.color}-${row.share}`}
                variant="keyValue"
                density="compact"
                enableSorting={false}
                className={styles.legendTable}
                tableClassName={styles.legend}
            />
        </div>
    );
}
